<script setup lang="ts">
import { PropType, ref, useAttrs, reactive } from "vue";
import { odsPill, PillTheme } from "../../components/odsPill";
import { InputType } from "./odsInput";
import { IconName } from "../../components/odsIcon";
import { odsFieldMessage } from "../../components/odsFieldMessage";

const emit = defineEmits(['mouseover', 'mouseout', 'blur', 'focus', 'change', 'keyup', 'keypress', 'keydown'])

defineExpose({
  reset() {
      value.value = ""
  }
})

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
  type: {
    type: String as PropType<typeof InputType[keyof typeof InputType]>,
    default: InputType.Text,
  },
  defaultValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  messageTheme: {
    type: String as PropType<typeof PillTheme[keyof typeof PillTheme]>,
    default: PillTheme.Default,
  },
  validators: {
    type: Array as PropType<{ label: string; theme: (value: string) => PillTheme; }[]>,
    default: [],
  },
  defaultValueFiresChangeEvent: {
    type: Boolean,
    default: false
  },
  autocomplete: {
    type: [Boolean, String],
    default: true
  }
});

const value = ref(props.defaultValue);

const input = ref();

const attrs = reactive(
    {
        ...useAttrs(),
        onFocus: (event : Event) => focusInput(event),
        onBlur: (event : Event) => blurInput(event),
        onMouseover: (event : Event) => mouseover(event),
        onMouseout: (event : Event) => mouseout(event),
    }
)


function getValidatorIcon(theme) {
  switch (theme) {
    case PillTheme.Success:
      return IconName.Check;
    case PillTheme.Warning:
      return IconName.Exclamation;
    case PillTheme.Failure:
      return IconName.Close;
    default:
      return undefined;
  }
}

function mouseover(event : Event){
    emit('mouseover', event)
}
function mouseout(event : Event){
    emit('mouseout', event)        
}
function blurInput(event : Event){
    emit('blur',event)
}
function focusInput(event : Event){
    emit('focus', event)
}


if(props.defaultValueFiresChangeEvent){
  emit('change', value.value)
}

</script>

<template>
  <div class="ods-input"
    :class="[{ disabled, error: messageTheme === PillTheme.Failure, warning: messageTheme === PillTheme.Warning }]">
    <label v-html="label" :for="id" />
    <div class="input" @click="input.focus()">
      <input :autocomplete="autocomplete === false ? 'off' : (autocomplete === true ? 'on' : autocomplete)" :name="id" v-bind="attrs" :id="id" :type="type" @change.stop="$emit('change', value)" @keyup="$emit('keyup', value)" @keypress="$emit('keypress', $event)" @keydown="$emit('keydown', $event)" v-model="value" ref="input"
        :class="[{ disabled }]" :placeholder="placeholder" :disabled="disabled">
      <slot name="icon" />
    </div>
    <div v-if="validators.length > 0" class="conditions">
      <odsPill v-for="(validator, index) of validators" :key="index" :theme="validator.theme(value)"
        :icon="getValidatorIcon(validator.theme(value))" :label="validator.label" />
    </div>
    <odsFieldMessage :message="message" :message-theme="messageTheme" />
    <div class="input-after">
      <slot name="input-after"></slot>
    </div>
  </div>
</template>

<style lang="scss" src="./odsInput.scss" />